import React, { useEffect, useState, useRef } from 'react';
import './Help.scss';

type Section = {
  id: string;
  title: string;
  content: React.ReactNode;
};

const sections: Section[] = [
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p>
          Here we&apos;re going to go through everything you can use
          ViewEventNotes for.
        </p>
        <p>
          Hopefully everything will be covered here, but if not, please just get
          in touch and we&apos;ll happily talk you through what you&apos;re
          trying to do.
        </p>
      </>
    )
  },
  {
    id: 'terminology',
    title: 'Terminology',
    content: (
      <>
        <p>
          Words can be tricky, so we&apos;ll explain some of the terms we use
          here, so you can understand what we&apos;re talking about, before we
          go any further.
        </p>
        <p>
          <strong>Participants</strong> - these are the lovely people who you
          want to show your <strong>content</strong> to, as part of an{' '}
          <strong>event</strong>.
        </p>
        <p>
          <strong>Event</strong> - this is the event that&apos;s taking place at
          some point. This is what your <strong>participants</strong> will be
          attending, and you will be providing the <strong>content</strong> for.
        </p>
        <p>
          <strong>Content</strong> - the content of the <strong>event</strong>{' '}
          is whatever you want to present to your <strong>participants</strong>.
          It is made up of a number of <strong>slides</strong>, which you will
          be showing to your <strong>participants</strong> when it&apos;s the
          right time.
        </p>
        <p>
          <strong>Slide</strong> - a slide is a single page of{' '}
          <strong>content</strong> that you will be presenting during your{' '}
          <strong>event</strong>. It can contain text and images.
        </p>
        <p>
          <strong>Showing</strong> - you may want to present the same{' '}
          <strong>event</strong> multiple times (maybe you&apos;re doing a tour,
          or running the <strong>event</strong> a second time for anyone who
          missed the first one). You can create multiple showings which can be
          presented individually. When a participant joins an{' '}
          <strong>event</strong> they will be asked which showing they are
          taking part in, from a list of all available showings.
        </p>
        <p>
          <strong>Welcome Screen</strong> - this is the page first shown to{' '}
          <strong>participants</strong> when they join your{' '}
          <strong>event</strong>. It shows what the <strong>event</strong> is
          all about, tells them how the app works, and lets them join.
        </p>
        <p>
          <strong>End Screen</strong> - this is the page that is shown to{' '}
          <strong>participants</strong> when the <strong>event</strong> has
          finished. It will thank them, show them any links you have set up, and
          lets them view any additional pages that you have added.
        </p>
      </>
    )
  },
  {
    id: 'creating',
    title: 'Creating events',
    content: (
      <>
        <p>
          From your dashboard, press the &apos;Create new event&apos; button.
        </p>
        <img
          src="/images/help/create-new-session-1.png"
          alt="The 'Create new event' button on a dashboard"
        />
        <p>You can then start filling in the details of your event.</p>
        <img
          src="/images/help/create-new-session-2.png"
          alt="The 'Create new event' wizard - setting the details"
        />
        <p>
          <strong>Event name</strong> - this is the name of your event. Your
          participants will see this name when they join the event.
        </p>
        <p>
          <strong>Event date</strong> - if you know the date when your event is
          going to take place, you can select the start date and time here. This
          will also be shown to participants when they join. However, this is
          optional - if you don&apos;t provide anything here, everything will
          still work fine, and the event&apos;s start date and time will be
          based on when you first start presenting it.
        </p>
        <p>You can them press the &apos;Next: Theme&apos; button.</p>
        <img
          src="/images/help/create-new-session-3.png"
          alt="The 'Create new event' wizard - setting the theme"
        />
        <p>
          <strong>Font</strong> - this is the font used to show your text to
          your participants.
        </p>
        <p>
          <strong>Colour</strong> - this is the colour scheme to use, including
          the background colour and the colour of the buttons.
        </p>
        <p>
          Once you&apos;re happy, press the &apos;Next: Add content&apos; button
          to start adding your content.
        </p>
      </>
    )
  },
  {
    id: 'editing',
    title: 'Editing events',
    content: (
      <>
        <p>
          Events can be edited via the menu on the event editor. From your
          dashboard, press an event to get to the event editor.
        </p>
        <p>Press the three-dot menu to open the event settings.</p>
        <img
          src="/images/help/edit-session-menu.png"
          alt="The event editor menu"
        />
        <p>You can edit the event details here.</p>
        <img
          src="/images/help/edit-session-details.png"
          alt="Editing the event details"
        />
        <p>
          <strong>Event name</strong> - you can edit the event name here.
        </p>
        <p>
          <strong>Allow User Navigation</strong> - when this is on, participants
          are able to navigate back to previous slides whilst the event is being
          presented. When off, the participants have no control over this and
          just get to see the current content.
        </p>
        <p>
          <strong>Allow View All Pages</strong> - when this is on, participants
          are able to see all the slides in the event, even before they have
          been presented. When off, participants can only see slides that have
          been previously been presented.
        </p>

        <p>You can also edit the event theme here.</p>
        <img
          src="/images/help/edit-session-style.png"
          alt="Editing the event style"
        />

        <p>These are the same options as when the event was being created.</p>

        <p>Via this menu, you can also delete the event.</p>
        <img
          src="/images/help/edit-session-delete.png"
          alt="Deleting an event"
        />
      </>
    )
  },
  {
    id: 'sharing',
    title: 'Sharing events',
    content: (
      <>
        <p>From the event editor, you can share the event.</p>
        <img src="/images/help/session-actions.png" alt="The event actions" />

        <p>
          <strong>Share</strong> - this gives you options to share this event
          with your participants.
        </p>
        <img
          src="/images/help/session-actions-share.png"
          alt="Sharing an event"
        />
        <p>
          Here is a QR code that, once scanned, takes the participant to the
          event welcome screen. This QR code can be clicked here, and it can be
          downloaded as an image for you to print out.
        </p>
        <p>
          An access code is also shown, which allowed participants to access the
          event without using the QR code. They need to go to the website shown
          and enter the code.
        </p>

        <img
          src="/images/help/session-actions-share-code.png"
          alt="Entering in the event code"
        />
      </>
    )
  },
  {
    id: 'welcome',
    title: 'Welcome screen',
    content: (
      <>
        <p>
          Once you&apos;ve created an event, you can add content to it. From
          your dashboard, press an event to get to the event editor.
        </p>
        <img src="/images/help/adding-content-1.png" alt="The event editor" />
        <p>
          This first page you see is for the Welcome Screen, where you can
          decide what your participants will see when they join the event. You
          can see what the welcome screen will look like as you make changes in
          the preview window at the bottom.
        </p>
        <p>
          <strong>Header image</strong> - this is the image shown at the top of
          the welcome screen, behind the logo. This needs to be no larger than
          5mb, and a decent size so that it appears nicely for all participants.
        </p>
        <p>
          <strong>Header logo</strong> - this is a smaller logo that appears in
          front of the header image. This also needs to be no larger than 5mb,
          but does not have to be really large.
        </p>
      </>
    )
  },
  {
    id: 'about',
    title: 'About screen',
    content: (
      <>
        <p>
          The about screen is an optional page which is available from the
          welcome screen, and is for you to provide more information to your
          participants about this event.
        </p>
        <img
          src="/images/help/adding-content-2.png"
          alt="The about screen editor"
        />
        <p>
          This is the standard content editor (see{' '}
          <a href="#contenteditor">Content editor</a> for how this works).
        </p>
      </>
    )
  },
  {
    id: 'end',
    title: 'End screen',
    content: (
      <>
        <p>
          Selecting &apos;End screen&apos; will let you configure the ending of
          your event.
        </p>
        <img
          src="/images/help/adding-content-3.png"
          alt="The end screen editor"
        />
        <p>
          Here you can set the various links that can be shown when the event
          has ended.
        </p>
        <p>
          You can also add custom pages which are linked from the end screen,
          and lets you add content for your participants to view after the
          event.
        </p>
        <p>
          As with the welcome screen editor, there is a preview window for you
          to see what your changes will look like.
        </p>
      </>
    )
  },
  {
    id: 'slides',
    title: 'Slides',
    content: (
      <>
        <p>
          The content for an event is made up of a number of slides. Press
          &apos;Add Slide&apos; to add a new slide, or select &apos;New
          Slide&apos; which is added automatically when you create an event.
        </p>
        <img src="/images/help/adding-content-4.png" alt="The content editor" />
        <p>
          This is the standard content editor (see{' '}
          <a href="#contenteditor">Content editor</a> for how this works).
        </p>
        <p>
          You can also change the name of the slide here. Set the name of each
          slide carefully, so that you know which slide is which.
        </p>
        <p>Press the edit icon to edit the name.</p>
        <img
          src="/images/help/adding-content-edit-icon.png"
          alt="The edit name icon"
        />
        <p>Alternatively, you can just double-click the name to edit it.</p>
        <img
          src="/images/help/adding-content-editing-name.png"
          alt="Editing the name"
        />
        <p>
          Once editing, you can change the name. Once you&apos;ve finished,
          either press the tick icon, or the enter key. To cancel editing, press
          the cross icon.
        </p>
        <p>To remove the slide, press the delete icon.</p>
        <img
          src="/images/help/adding-content-delete-icon.png"
          alt="The delete icon"
        />
      </>
    )
  },
  {
    id: 'contenteditor',
    title: 'Content Editor',
    content: (
      <>
        <p>
          The content editor is used for adding content to slides, as well as to
          the about screen and the end screen pages.
        </p>
        <img src="/images/help/adding-content-4.png" alt="The content editor" />
        <p>Start off by pressing the central add icon.</p>
        <img
          src="/images/help/content-editor-add-icon.png"
          alt="The add icon"
        />
        <p>You get the choice of adding either text, or an image.</p>
        <img
          src="/images/help/content-editor-add-icon-options.png"
          alt="The options when adding content"
        />
        <p>
          Selecting the text icon will add a text block for you to enter the
          text you want to show.
        </p>
        <img src="/images/help/content-editor-text.png" alt="The text editor" />
        <p>There are some options here.</p>
        <p>
          Next to the textbox there are buttons for selecting whether the text
          should be a main header, a sub header or standard text.
        </p>
        <img
          src="/images/help/content-editor-text-types.png"
          alt="The text types"
        />
        <p>For standard text, you can add basic formatting.</p>
        <img
          src="/images/help/content-editor-text-formatting.png"
          alt="The text formatting options"
        />
        <p>
          You can make text bold or italic, and create lists (either with bullet
          points or numbers). This uses the <strong>Markdown</strong> syntax.
        </p>
        <img
          src="/images/help/content-editor-text-markdown.png"
          alt="Example of Markdown formatting"
        />

        <p>This will be shown to the participants like this:</p>
        <img
          src="/images/help/content-editor-text-markdown-render.png"
          alt="Example of Markdown formatting being shown to participants"
        />

        <p>If you had pressed the image icon, you can upload an image.</p>
        <img
          src="/images/help/content-editor-image.png"
          alt="The image selector"
        />

        <p>To remove a text or image block, press the delete icon.</p>
        <img
          src="/images/help/content-editor-delete.png"
          alt="The delete icon"
        />

        <p>
          You can add extra text or images anywhere in the page via the add
          icons.
        </p>
        <img
          src="/images/help/content-editor-multiple.png"
          alt="Multiple text blocks in a page"
        />

        <p>
          On each row, there is an icon to toggle whether the row is shown to
          participants by default, or if it is hidden behind a &apos;Show
          more&apos; link.
        </p>
        <img
          src="/images/help/content-editor-show-default.png"
          alt="Icon to show the row by default"
        />

        <p>This icon can be toggled on</p>
        <img
          src="/images/help/content-editor-show-more.png"
          alt="Icon to hide the row behind a 'Show more' link"
        />

        <p>When toggled on, the content will be shown behind a link</p>
        <img
          src="/images/help/content-editor-show-more-preview-closed.png"
          alt="Content hidden behind a link"
        />

        <p>When expanded, the content is shown</p>
        <img
          src="/images/help/content-editor-show-more-preview-open.png"
          alt="Content shown after being expanded"
        />
      </>
    )
  },
  {
    id: 'showings',
    title: 'Showings',
    content: (
      <>
        <p>Showings are a way of presenting the same event multiple times.</p>
        <p>
          When an event is presented, it has a specific start date/time and the
          order in which the slides are presented are remembered for that event,
          for participants to review later. If you wanted to present the same
          event again, instead of re-opening the event, it&apos;s best to create
          a new showing.
        </p>
        <p>
          Showings are presented individually, so the presentation of one
          showing does not affect any of the other showings.
        </p>

        <p>You can access the showings for an event from the event editor.</p>
        <img src="/images/help/session-actions.png" alt="The event actions" />

        <p>
          By default, an event has no showings set up. When you start presenting
          the event, a new showing will be auto-generated, using the current
          date/time.
        </p>
        <p>You can add new showings via the &apos;Add showing&apos; button.</p>
        <img src="/images/help/session-showings.png" alt="The showings page" />
        <p>
          You can set the date and time of the showing, along with an optional
          location. The location can be useful if the events are being held in
          different physical locations, so this can help your participants know
          which event they should be joining.
        </p>
        <img
          src="/images/help/session-showings-add.png"
          alt="Adding a new showing"
        />

        <p>
          Once a showing has been added, it can be edited or deleted through the
          menu.
        </p>
        <img
          src="/images/help/session-showings-edit.png"
          alt="The showing menu"
        />

        <p>
          If you have multiple showings, participants will be able to select
          which showing they want to join.
        </p>
        <img
          src="/images/help/session-showings-example.png"
          alt="Example showings"
        />

        <p>This will appear as a list to the participants.</p>
        <img
          src="/images/help/session-showings-example-client.png"
          alt="Example showings for participants"
        />

        <p>
          You can present this showing either by pressing the showing card
          itself, or via its menu.
        </p>
      </>
    )
  },
  {
    id: 'preview',
    title: 'Previewing',
    content: (
      <>
        <p>
          Once you have created an event, you can preview it to see how it
          looks.
        </p>

        <p>You can access the preview for an event from the event editor.</p>
        <img src="/images/help/session-actions.png" alt="The event actions" />
        <p>When previewing, you see the presentation page.</p>
        <img src="/images/help/session-preview.png" alt="The event preview" />
        <p>
          Everything works the same as a proper presentation. See{' '}
          <a href="#present">Presenting</a> for details on how this page works.
          If you want to join the preview as a participant, you can access the
          preview-specific QR code via the &apos;Share&apos; button.
        </p>
        <img
          src="/images/help/session-preview-share.png"
          alt="The preview QR code"
        />
        <p>
          Note that if you refresh the preview presentation page, you can
          continue presenting the same preview. However, if you leave the page
          and press the &apos;Preview&apos; button again, you will generate a
          brand new preview that is starting from scratch, with a new preview QR
          code.
        </p>
      </>
    )
  },
  {
    id: 'present',
    title: 'Presenting',
    content: (
      <>
        <p>
          Once you have created an event, and you&apos;re event is ready to go,
          you can present it.
        </p>

        <p>You can present the event from the event editor.</p>
        <img src="/images/help/session-actions.png" alt="The event actions" />
        <p>
          Note that if you have multiple showings, you will be taken to the
          showings page first to let you select which showing you are
          presenting.
        </p>

        <img
          src="/images/help/session-present.png"
          alt="The event presentation page"
        />
        <p>There are many elements to this presentation page.</p>
        <p>
          The taskbar shows the name of your event, the details of the showing,
          a preview of what the participants are currently seeing, and the list
          of slides.
        </p>
        <img src="/images/help/session-present-taskbar.png" alt="The taskbar" />
        <p>
          The header shows the state of the event (either paused, live, or
          ended). You can also access the QR code and access code via the
          &apos;Share&apos; button.
        </p>
        <img src="/images/help/session-present-header.png" alt="The header" />
        <p>
          The navigation controls lets you present different slides, by showing
          the next or previous slide. You can blank/resume the event if you need
          to. This area also shows the number of connected participants (which
          will include yourself when you are presenting).
        </p>
        <img
          src="/images/help/session-present-navigation.png"
          alt="The navigation controls"
        />
        <p>
          The main area of the presentation page is the list of slides. You can
          click the individual &apos;Present&apos; buttons to show specific
          slides.
        </p>
        <img src="/images/help/session-present-slides.png" alt="The slides" />

        <p>
          To start a presentation, press the &apos;Start presentation&apos;
          button.
        </p>
        <img src="/images/help/session-present-header.png" alt="The header" />

        <p>This will start the event.</p>
        <img
          src="/images/help/session-present-started.png"
          alt="The event started"
        />

        <p>
          Currently, nothing is being presented. To show some content, you can
          either press the &apos;Next section&apos; button, or press the
          individual &apos;Present&apos; button on a slide
        </p>
        <img
          src="/images/help/session-present-navigation-started.png"
          alt="The next button is available to press"
        />
        <p>
          When a slide is being presented, you will see that the slide is
          displayed with a &apos;Presenting&apos; icon, and the contents of the
          slide will be seen in the participant preview.
        </p>
        <img
          src="/images/help/session-present-showing-slide.png"
          alt="A slide is being presented"
        />
        <p>
          If you want to temporarily hide the content from the participants,
          press the &apos;Hold&apos; button.
        </p>
        <img
          src="/images/help/session-present-navigation-started.png"
          alt="The next button is available to press"
        />
        <p>
          The content will be hidden from the participants, and the status of
          the event will change to &apos;Event paused&apos;.
        </p>
        <img src="/images/help/session-present-on-hold.png" alt="On hold" />
        <p>
          To resume the event, press the &apos;Resume&apos; button, or press the
          &apos;Previous section&apos;, &apos;Next section&apos;, or a slide
          &apos;Present&apos; button.
        </p>
        <p>
          When the event has ended, press the &apos;End event&apos; button. This
          will show the end screen to the participants, and the status of the
          event will change to &apos;Event ended&apos;.
        </p>
        <img
          src="/images/help/session-present-ended.png"
          alt="The event has ended"
        />
        <p>
          If you want to reopen the event to continue where you left off last,
          press the &apos;Reopen event&apos; button.
        </p>
        <p>
          Participants will still be able to access the event after it has
          ended, but will initially see the end screen. They can press the
          &apos;Back&apos; button to review the content that was presented for
          that event.
        </p>
      </>
    )
  },
  {
    id: 'contact',
    title: 'Contact support',
    content: (
      <p>
        If you need further assistance, contact us at{' '}
        <a href="mailto:vieweventnotes@bungalow64.co.uk">
          vieweventnotes@bungalow64.co.uk
        </a>
        .
      </p>
    )
  }
];

const HelpPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  useEffect(() => {
    const hash = window.location.hash.slice(1); // remove the '#'

    if (hash) {
      // delay scrolling a bit to ensure layout is ready
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // or use your utility: smoothScrollTo(hash, 500);
        }
      }, 100); // adjust as needed
    }
  }, []);
  // TODO: Don't duplicate from PresentShowing.tsx
  function smoothScrollTo(targetId: string, duration = 500): void {
    const target = document.getElementById(targetId);
    if (!target) return;

    const container = document.querySelector<HTMLElement>('.content');
    if (!container) return;

    const containerHeight = container.clientHeight;
    const targetPosition =
      target.offsetTop -
      container.offsetTop -
      containerHeight / 2 +
      target.clientHeight / 2;
    const startPosition = container.scrollTop;
    const distance = targetPosition - startPosition;
    let startTime: number | null = null;

    function animationStep(currentTime: number): void {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      const ease =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

      if (container) {
        container.scrollTop = startPosition + distance * ease;
      } else {
        return;
      }

      if (timeElapsed < duration) {
        requestAnimationFrame(animationStep);
      }
    }

    requestAnimationFrame(animationStep);
  }
  useEffect(() => {
    if (!window.IntersectionObserver) {
      // Fallback or simple default
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Filter entries that are intersecting
        const visibleSections = entries
          .filter((e) => e.isIntersecting)
          // Sort by viewport position (top to bottom)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleSections.length > 0) {
          setActiveSection(visibleSections[0].target.id);
          history.replaceState(null, '', `#${visibleSections[0].target.id}`);
        }
      },
      {
        root: null, // viewport (or you can specify your scroll container element here)
        rootMargin: '-30% 0px -70% 0px', // adjust to trigger when section is roughly top third of viewport
        threshold: 0
      }
    );

    sections.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => {
      sections.forEach(({ id }) => {
        const el = sectionRefs.current[id];
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  const handleMenuClick = (id: string) => {
    smoothScrollTo(id, 500);
    setActiveSection(id);
  };

  return (
    <div className="help-page">
      <main className="help-content">
        {sections.map(({ id, title, content }) => (
          <section
            key={id}
            id={id}
            ref={(el) => (sectionRefs.current[id] = el)}
            className="help-section"
          >
            <h2>{title}</h2>
            {content}
          </section>
        ))}
      </main>

      <nav className="help-menu" aria-label="Help section navigation">
        <ul>
          {sections.map(({ id, title }) => (
            <li key={id}>
              <button
                type="button"
                className={id === activeSection ? 'active' : ''}
                onClick={() => handleMenuClick(id)}
              >
                {title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};
export default HelpPage;
